'use client';

import { useEffect, useState } from 'react';
import { useTechnology } from '@/hooks/technology';
import type { ISerialPort } from '@/hooks/useSerialPort';
import { fetchWithAuth, getUserFromToken } from '@/lib/api';
import type { Device } from '@/types/production/device';
import type { IFirmwareUpdateLog } from '@/types/production/firmware-update-log';
import type { ITechnology } from '@/types/production/tool';

namespace Namespace {
  export interface UseFirmwareUpdateProps {
    technology: ITechnology | null;
  }

  export interface Detected {
    port: ISerialPort;
    equipment: Equipment;
    status: 'fully_identified' | 'partially_identified' | 'not_identified';
  }

  interface Equipment {
    imei?: string | undefined;
    iccid?: string | undefined;
    firmware?: string | undefined;
    serial?: string | undefined;
    lora_keys?: Partial<Device['Equipment']['lora_keys']>;
  }

  export interface FirmwareUpdate extends IFirmwareUpdateLog {}
}

export const useFirmwareUpdate = (props: Namespace.UseFirmwareUpdateProps) => {
  const { technology } = props;

  const [toDetect, setToDetect] = useState<boolean>(true);
  const [detected, setDetected] = useState<Namespace.Detected[]>([]);
  const [isDetecting, setIsDetecting] = useState<boolean>(false);

  const [updated, setUpdated] = useState<Namespace.FirmwareUpdate[]>([]);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // hook that handle interactions with devices
  const { ports, handleDetection, handleFirmwareUpdate, requestPort, isIdentified } =
    useTechnology(technology);

  // function that handle the configuration process, check if the process was successful and save result on database
  const update = async (detected: Namespace.Detected[]) => {
    setIsUpdating(true);

    try {
      const validDetected = detected.filter(
        (
          i,
        ): i is (typeof detected)[number] & {
          equipment: { serial: string; firmware: string };
        } => Boolean(i.equipment.serial && i.equipment.firmware),
      );

      const firmwareUpdateResult = await handleFirmwareUpdate(validDetected);

      // check if each message sent has response and recorded to the desired profile
      const result = firmwareUpdateResult
        .map(({ messages, status, equipment, init_time, end_time }) => {
          const user = getUserFromToken();
          if (!user) {
            throw new Error('User not authenticated');
          }

          const configuration_log: Omit<IFirmwareUpdateLog, 'id' | 'created_at'> = {
            equipment,
            init_time,
            end_time,
            status,
            technology: {
              id: technology?.id ?? '',
              system_name: technology?.name.system ?? '',
            },
            messages,
            user,
          };

          return configuration_log;
        })
        .filter((el): el is NonNullable<typeof el> => el !== undefined);

      // save result on database
      const res = await fetchWithAuth('/api/production/firmware-update-logs/batch', {
        method: 'POST',
        body: JSON.stringify({ logs: result }),
      });
      if (!res.ok) throw new Error('Erro ao salvar log de firmware');
      const dataSavedOnDb = await res.json();

      // update state with configuration process result
      setUpdated((prev) => prev.concat(dataSavedOnDb));
    } catch (error) {
      console.error('[ERROR] update use-firmware-update', error);
    } finally {
      setToDetect(false);
      setIsUpdating(false);
    }
  };

  // useEffect used to identify devices when connected via serial ports
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!toDetect) return;

      if (isUpdating) return;

      if (!isDetecting && ports.length) {
        setIsDetecting(true);
        const detected = await handleDetection(ports);

        setDetected((prev) => {
          const map = new Map(prev.map((d) => [d.port, d]));

          for (const { port, response } of detected) {
            map.set(port, {
              port,
              equipment: response!,
              status: isIdentified(response!),
            });
          }

          return Array.from(map.values());
        });

        setIsDetecting(false);
      } else if (!isDetecting && !ports.length) {
        setDetected([]);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [ports, handleDetection, isUpdating, isDetecting, toDetect, isIdentified]);

  return {
    updated,
    detected,
    update,
    requestPort,
    isUpdating,
    isDetecting,
    toDetect,
    setToDetect,
  };
};

export default useFirmwareUpdate;
